/**
 * SalesDashboard — sales team pane of glass, four tabs:
 *
 *   Tentpole Calendar — network content calendar: tentpole curation, creator
 *                       submission review queue, admin quick-add entries.
 *   Media Plans       — the Media Plan Builder: advertiser + campaign +
 *                       audiences → server quote (rate card + historical rate
 *                       suggestion) → review/edit → saved plan with a Google
 *                       Sheet and client-facing PDF. Kept mounted once visited
 *                       so a half-built draft survives a tab switch.
 *   Rate Cards        — sell-side rate cards + the advertiser registry
 *                       (read for everyone with sales.view; edits need
 *                       `mediaPlans.manage`). Also kept mounted once visited.
 *                       Both tabs share one copy of the reference data and the
 *                       advertiser registry (useMediaPlanRegistry).
 *   Pipeline          — the secondary read-only pipeline view (RFPs in flight,
 *                       IO delivery health, follow-up queue, activity).
 *
 * Deep links: ?tab=<key> selects a tab (missing/legacy values land on the
 * Tentpole Calendar); ?entry=<id> opens the entry detail modal (used by the internal
 * submission-notification email) and forces the calendar tab; ?plan=<id> opens a
 * media plan and forces the Media Plans tab; ?new=1 is the plan builder (kept
 * across tab switches so Back closes it), and ?draft=<id> opens a brief priced
 * in Culture Genius chat in it (then becomes ?new=1). Reads are gated by `sales.view`;
 * calendar writes additionally need `tentpoleCalendar.manage` (write controls
 * hidden without it).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, DashboardFrame, Tabs, Toast, ToastKind, ToastLayer } from '../ui';
import { usePermissions } from '../../hooks/usePermissions';
import { useScrollTopOnChange } from '../../hooks/useScrollTopOnChange';
import {
  getSalesOverview,
  getSalesIoDelivery,
  SalesOverviewResponse,
  IoDeliveryResponse,
} from '../../services/salesApi';
import {
  getCalendarPendingCount,
  listCalendarEntries,
  listCalendarReviewQueue,
  listTentpoles,
  updateCalendarEntry,
} from '../../services/tentpoleCalendarApi';
import { CalendarEntry, Tentpole } from './TentpoleCalendar/types';
import { calendarEntryEffectiveEndYmd } from './TentpoleCalendar/shared';
import OverviewTab from './OverviewTab';
import CalendarTab, { CalendarFiltersState } from './TentpoleCalendar/CalendarTab';
import EntryDetail from './TentpoleCalendar/EntryDetail';
import TentpoleEditor from './TentpoleCalendar/TentpoleEditor';
import EntryEditor from './TentpoleCalendar/EntryEditor';
import MediaPlansTab, { MediaPlansNavigate } from './MediaPlans/MediaPlansTab';
import { useMediaPlanRegistry } from './MediaPlans/useMediaPlanRegistry';
import RateCardsTab from './RateCards/RateCardsTab';
import {
  loadCursorDepth,
  loadPageIfCurrent,
  scheduleVisibleCalendarRefresh,
} from './TentpoleCalendar/calendarRefreshScheduler';
import {
  SALES_DASHBOARD_TAB_ORDER,
  SalesDashboardTabKey as TabKey,
  isMediaPlanBuilderOpen,
  mediaPlanChatDraftId,
  resolveInitialSalesDashboardTab,
} from './salesDashboardTabs';

// Auto-refresh cadence — paused when the browser tab is hidden or a
// non-pipeline dashboard tab is active, so we don't burn GAM quota.
const AUTO_REFRESH_MS = 5 * 60 * 1000;
const CALENDAR_PAGE_SIZE = 200;
const REVIEW_QUEUE_PAGE_SIZE = 50;
const STALE_AFTER_MS = 30 * 60 * 1000;
const TOAST_MS = 5000;

/** Append a cursor page without duplicating rows already loaded. */
const mergeEntryPages = (current: CalendarEntry[], incoming: CalendarEntry[]): CalendarEntry[] => {
  const byId = new Map(current.map((entry) => [entry.id, entry]));
  const order = current.map((entry) => entry.id);
  for (const entry of incoming) {
    if (!byId.has(entry.id)) order.push(entry.id);
    // Preserve fields a sanitized page omitted while accepting newer list data.
    byId.set(entry.id, { ...byId.get(entry.id), ...entry });
  }
  return order.map((id) => byId.get(id)!);
};

const SalesDashboard: React.FC = () => {
  const { has } = usePermissions();
  const canManage = has('tentpoleCalendar.manage');
  const canManagePlans = has('mediaPlans.manage');

  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<TabKey>(() => resolveInitialSalesDashboardTab(searchParams));
  const tabRef = useRef<TabKey>(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);

  // One toast at a time, raised by every tab and modal through `notify`. The
  // timer ref stops an older toast's timeout from hiding a newer one early
  // (e.g. a second validation error a few seconds after the first).
  const [toast, setToast] = useState<{ kind: ToastKind; title: string; message?: string } | null>(null);
  const toastTimer = useRef<number | null>(null);
  const notify = useCallback((kind: 'success' | 'error', title: string, message?: string) => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    setToast({ kind, title, message });
    toastTimer.current = window.setTimeout(() => setToast(null), TOAST_MS);
  }, []);
  useEffect(
    () => () => {
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  // --- overview domain ----------------------------------------------------------
  const [overview, setOverview] = useState<SalesOverviewResponse | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [ioDelivery, setIoDelivery] = useState<IoDeliveryResponse | null>(null);
  const [ioError, setIoError] = useState<string | null>(null);
  const [ioLoading, setIoLoading] = useState(true);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const result = await getSalesOverview();
      setOverview(result);
    } catch (err: any) {
      setOverviewError(err?.message || 'Failed to load sales overview.');
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  const loadIoDelivery = useCallback(async () => {
    setIoLoading(true);
    setIoError(null);
    try {
      const result = await getSalesIoDelivery();
      setIoDelivery(result);
    } catch (err: any) {
      setIoError(err?.message || 'Failed to load IO delivery health.');
    } finally {
      setIoLoading(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    loadOverview();
    loadIoDelivery();
  }, [loadOverview, loadIoDelivery]);

  useEffect(() => {
    // Pipeline is secondary and GAM-backed, so do not load it merely because
    // the default Calendar screen mounted. Direct Pipeline links still load.
    if (tabRef.current === 'pipeline') refreshAll();
  }, [refreshAll]);

  // Auto-refresh on an interval, but only while the pipeline tab is both
  // visible and active.
  useEffect(() => {
    const tick = () => {
      if (
        tabRef.current === 'pipeline' &&
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible'
      ) {
        refreshAll();
      }
    };
    const id = window.setInterval(tick, AUTO_REFRESH_MS);
    const onVisibilityChange = () => {
      if (tabRef.current === 'pipeline' && document.visibilityState === 'visible') refreshAll();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refreshAll]);

  const generatedAt = overview?.generatedAt ?? ioDelivery?.generatedAt ?? null;
  const isStale = generatedAt ? Date.now() - new Date(generatedAt).getTime() > STALE_AFTER_MS : false;

  // --- calendar domain ------------------------------------------------------------
  // Eager-loaded on mount: the tab badge needs the pending count, and these are
  // cheap Firestore reads (unlike the GAM-backed overview).
  const [tentpoles, setTentpoles] = useState<Tentpole[]>([]);
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarLoadError, setCalendarLoadError] = useState<string | null>(null);
  const [calendarNextCursor, setCalendarNextCursor] = useState<string | null>(null);
  const [loadingMoreEntries, setLoadingMoreEntries] = useState(false);
  const [calendarRefreshing, setCalendarRefreshing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [reviewEntries, setReviewEntries] = useState<CalendarEntry[]>([]);
  const [reviewNextCursor, setReviewNextCursor] = useState<string | null>(null);
  const [reviewLoading, setReviewLoading] = useState(true);
  const [reviewLoadingMore, setReviewLoadingMore] = useState(false);
  const calendarRefreshGeneration = useRef(0);
  const calendarRefreshInFlight = useRef(false);
  const entryPagesLoaded = useRef(1);
  const entryLoadMoreGeneration = useRef(0);
  const entryLoadMoreInFlight = useRef(false);
  const reviewPagesLoaded = useRef(1);
  const reviewLoadMoreGeneration = useRef(0);
  const reviewLoadMoreInFlight = useRef(false);
  const [calFilters, setCalFilters] = useState<CalendarFiltersState>({
    status: '',
    tentpoleSlug: '',
    creatorSlug: '',
    category: '',
    timeframe: 'upcoming',
    viewMode: 'tentpole',
  });

  const refreshCalendar = useCallback((options: { initial?: boolean; notifyOnError?: boolean } = {}) => {
    // A mutation-triggered refresh may overlap a timer/focus refresh. Only the
    // newest response is allowed to replace state, preventing stale races.
    const generation = ++calendarRefreshGeneration.current;
    // Any load-more request used a cursor from the pre-refresh pages. Invalidate
    // it before those pages are replaced so it cannot append stale rows afterward.
    reviewLoadMoreGeneration.current += 1;
    reviewLoadMoreInFlight.current = false;
    entryLoadMoreGeneration.current += 1;
    entryLoadMoreInFlight.current = false;
    calendarRefreshInFlight.current = true;

    if (options.initial) {
      setCalendarLoading(true);
      setReviewLoading(true);
    }
    setReviewLoadingMore(false);
    setLoadingMoreEntries(false);
    setCalendarRefreshing(true);

    const request = Promise.all([
      listTentpoles(true),
      // Reload every page "Load older entries" has opened, not just page one.
      loadCursorDepth(entryPagesLoaded.current, CALENDAR_PAGE_SIZE, listCalendarEntries),
      getCalendarPendingCount(),
      loadCursorDepth(
        reviewPagesLoaded.current,
        REVIEW_QUEUE_PAGE_SIZE,
        listCalendarReviewQueue,
      ),
    ])
      // Resolves true when the server answered, false when it failed (a 429
      // included), so the refresh schedule can back off.
      .then(([tps, entryPage, count, reviewPage]) => {
        if (generation !== calendarRefreshGeneration.current) return true;
        setTentpoles(tps);
        setEntries(entryPage.entries);
        setCalendarNextCursor(entryPage.nextCursor);
        setPendingCount(count);
        setReviewEntries(reviewPage.entries);
        setReviewNextCursor(reviewPage.nextCursor);
        setCalendarLoadError(null);
        return true;
      })
      .catch((err) => {
        if (generation !== calendarRefreshGeneration.current) return false;
        setCalendarLoadError(err?.message || 'Failed to load calendar');
        if (options.notifyOnError !== false) notify('error', 'Failed to load calendar', err?.message);
        return false;
      })
      .finally(() => {
        if (generation !== calendarRefreshGeneration.current) return;
        setCalendarLoading(false);
        setReviewLoading(false);
        setCalendarRefreshing(false);
        calendarRefreshInFlight.current = false;
      });
    return request;
  }, [notify]);

  useEffect(() => { refreshCalendar({ initial: true }); }, [refreshCalendar]);

  // Calendar submissions are time-sensitive. Keep the badge/review queue fresh
  // while this dashboard is visible, and refresh when focus returns (once the
  // tab has settled, at most as often as shouldRefreshCalendar allows).
  useEffect(() => scheduleVisibleCalendarRefresh(
    () => refreshCalendar({ notifyOnError: false }),
  ), [refreshCalendar]);

  const loadMoreEntries = useCallback(async () => {
    if (
      !calendarNextCursor
      || loadingMoreEntries
      || entryLoadMoreInFlight.current
      || calendarRefreshInFlight.current
    ) return;

    const refreshGeneration = calendarRefreshGeneration.current;
    const loadMoreGeneration = ++entryLoadMoreGeneration.current;
    const cursor = calendarNextCursor;
    entryLoadMoreInFlight.current = true;
    setLoadingMoreEntries(true);
    try {
      const page = await loadPageIfCurrent(
        () => listCalendarEntries({ limit: CALENDAR_PAGE_SIZE, cursor }),
        () => (
          refreshGeneration === calendarRefreshGeneration.current
          && loadMoreGeneration === entryLoadMoreGeneration.current
        ),
      );
      if (!page) return;

      setEntries((current) => mergeEntryPages(current, page.entries));
      setCalendarNextCursor(page.nextCursor);
      entryPagesLoaded.current += 1;
    } catch (err: any) {
      if (
        refreshGeneration === calendarRefreshGeneration.current
        && loadMoreGeneration === entryLoadMoreGeneration.current
      ) {
        notify('error', 'Failed to load more calendar plans', err?.message);
      }
    } finally {
      if (loadMoreGeneration === entryLoadMoreGeneration.current) {
        entryLoadMoreInFlight.current = false;
        setLoadingMoreEntries(false);
      }
    }
  }, [calendarNextCursor, loadingMoreEntries, notify]);

  const loadMoreReviewEntries = useCallback(async () => {
    if (
      !reviewNextCursor
      || reviewLoadingMore
      || reviewLoadMoreInFlight.current
      || calendarRefreshInFlight.current
    ) return;

    const refreshGeneration = calendarRefreshGeneration.current;
    const loadMoreGeneration = ++reviewLoadMoreGeneration.current;
    const cursor = reviewNextCursor;
    reviewLoadMoreInFlight.current = true;
    setReviewLoadingMore(true);
    try {
      const page = await loadPageIfCurrent(
        () => listCalendarReviewQueue({
          limit: REVIEW_QUEUE_PAGE_SIZE,
          cursor,
        }),
        () => (
          refreshGeneration === calendarRefreshGeneration.current
          && loadMoreGeneration === reviewLoadMoreGeneration.current
        ),
      );
      if (!page) return;

      setReviewEntries((current) => mergeEntryPages(current, page.entries));
      setReviewNextCursor(page.nextCursor);
      reviewPagesLoaded.current += 1;
      // The dedicated count endpoint remains authoritative. A queue total is a
      // useful same-request update when the backend includes it.
      if (page.total !== undefined) setPendingCount(page.total);
    } catch (err: any) {
      if (
        refreshGeneration === calendarRefreshGeneration.current
        && loadMoreGeneration === reviewLoadMoreGeneration.current
      ) {
        notify('error', 'Failed to load more submissions', err?.message);
      }
    } finally {
      if (loadMoreGeneration === reviewLoadMoreGeneration.current) {
        reviewLoadMoreInFlight.current = false;
        setReviewLoadingMore(false);
      }
    }
  }, [notify, reviewLoadingMore, reviewNextCursor]);

  const onEntryUpdated = useCallback((updated: CalendarEntry) => {
    setEntries((prev) => {
      const exists = prev.some((e) => e.id === updated.id);
      return exists ? prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)) : [updated, ...prev];
    });
    setReviewEntries((prev) => {
      if (updated.status !== 'submitted') return prev.filter((entry) => entry.id !== updated.id);
      const exists = prev.some((entry) => entry.id === updated.id);
      return exists
        ? prev.map((entry) => (entry.id === updated.id ? { ...entry, ...updated } : entry))
        : prev;
    });
  }, []);

  const onEntryDeleted = useCallback((entryId: string) => {
    // Remove immediately so a deleted approved entry cannot linger while the
    // authoritative queue/count refresh is in flight.
    setEntries((prev) => prev.filter((entry) => entry.id !== entryId));
    setReviewEntries((prev) => prev.filter((entry) => entry.id !== entryId));
  }, []);

  // --- entry detail deep link -----------------------------------------------------
  const openEntryId = searchParams.get('entry');
  const openEntrySummary = useMemo(
    () => (openEntryId
      ? reviewEntries.find((entry) => entry.id === openEntryId)
        ?? entries.find((entry) => entry.id === openEntryId)
        ?? null
      : null),
    [entries, openEntryId, reviewEntries],
  );
  const openEntryTentpole = useMemo(
    () => (openEntrySummary?.tentpoleSlug
      ? tentpoles.find((tentpole) => tentpole.slug === openEntrySummary.tentpoleSlug) ?? null
      : null),
    [openEntrySummary, tentpoles],
  );
  const openEntry = useCallback((id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('entry', id);
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);
  const closeEntry = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('entry');
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  // --- media plans + rate cards -----------------------------------------------------
  // ?plan=<id> is a plan page, ?new=1 the builder, neither the list. One URL
  // update per move, so opening a plan can't leave ?new=1 behind (or back).
  const openPlanId = searchParams.get('plan');
  const mediaPlansMode = isMediaPlanBuilderOpen(searchParams) ? 'builder' : 'list';
  // The list, builder and plan page share the tab's scrolling area: each opens at the top.
  const mediaPlansScrollRef = useScrollTopOnChange<HTMLDivElement>(openPlanId ? `plan:${openPlanId}` : mediaPlansMode);
  // Both tabs stay mounted once visited, so a draft or an open editor survives a tab switch.
  const [mediaPlansVisited, setMediaPlansVisited] = useState(tab === 'media-plans');
  const [rateCardsVisited, setRateCardsVisited] = useState(tab === 'rate-cards');
  useEffect(() => {
    if (tab === 'media-plans') setMediaPlansVisited(true);
    if (tab === 'rate-cards') setRateCardsVisited(true);
  }, [tab]);
  const mediaPlanRegistry = useMediaPlanRegistry(mediaPlansVisited || rateCardsVisited);
  const chatDraftId = mediaPlanChatDraftId(searchParams);
  const navigateMediaPlans: MediaPlansNavigate = useCallback((target, opts) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'media-plans');
    next.delete('plan');
    next.delete('new');
    next.delete('draft');
    if (target.view === 'plan') next.set('plan', target.planId);
    if (target.view === 'builder') next.set('new', '1');
    setSearchParams(next, { replace: opts?.replace ?? false });
    setTab('media-plans');
  }, [searchParams, setSearchParams]);

  // --- editor modals ----------------------------------------------------------------
  const [showTentpoleEditor, setShowTentpoleEditor] = useState(false);
  const [editingTentpole, setEditingTentpole] = useState<Tentpole | null>(null);
  const [showEntryEditor, setShowEntryEditor] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CalendarEntry | null>(null);
  const [presetTentpoleSlug, setPresetTentpoleSlug] = useState<string | null>(null);
  // Promote flow: the custom entry whose label is becoming a tentpole. When
  // set, TentpoleEditor opens pre-filled and its save auto-reassigns the entry.
  const [promoteSourceEntry, setPromoteSourceEntry] = useState<CalendarEntry | null>(null);

  const openAddEntry = useCallback((tentpoleSlug?: string) => {
    setEditingEntry(null);
    setPresetTentpoleSlug(tentpoleSlug ?? null);
    setShowEntryEditor(true);
  }, []);

  const changeTab = useCallback((next: TabKey) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    // A plan deep link only makes sense on the Media Plans tab.
    if (next !== 'media-plans') params.delete('plan');
    setSearchParams(params, { replace: true });
    // Load Pipeline on first use, or refresh it after a long stint away.
    if (next === 'pipeline' && (!generatedAt || isStale)) refreshAll();
  }, [searchParams, setSearchParams, generatedAt, isStale, refreshAll]);

  const tabOptions = useMemo(() => {
    const optionsByKey: Record<TabKey, { value: TabKey; label: React.ReactNode; count?: number }> = {
      calendar: { value: 'calendar', label: 'Tentpole Calendar', count: pendingCount > 0 ? pendingCount : undefined },
      'media-plans': { value: 'media-plans', label: 'Media Plans' },
      'rate-cards': { value: 'rate-cards', label: 'Rate Cards' },
      pipeline: { value: 'pipeline', label: 'Pipeline' },
    };
    return SALES_DASHBOARD_TAB_ORDER.map((key) => optionsByKey[key]);
  }, [pendingCount]);

  const [rateCardsAddSignal, setRateCardsAddSignal] = useState(0);

  const topBarByTab: Record<TabKey, React.ReactNode> = {
    pipeline: (
      <div style={topBarStyle}>
        {generatedAt && (
          <span style={{ ...timestampStyle, ...(isStale ? staleTimestampStyle : {}) }}>
            {isStale ? '⚠ Stale · ' : 'Updated '}
            {new Date(generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </span>
        )}
        <button onClick={refreshAll} style={refreshBtnStyle}>
          Refresh
        </button>
      </div>
    ),
    calendar: (
      <div style={topBarStyle}>
        <button
          onClick={() => refreshCalendar({ notifyOnError: true })}
          style={refreshBtnStyle}
          disabled={calendarRefreshing}
        >
          {calendarRefreshing ? 'Refreshing…' : 'Refresh'}
        </button>
        {canManage && (
          <>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => { setEditingTentpole(null); setShowTentpoleEditor(true); }}
            >
              + Add tentpole
            </Button>
            <Button variant="primary" size="sm" onClick={() => openAddEntry()}>
              + Add plan
            </Button>
          </>
        )}
      </div>
    ),
    'media-plans': (
      <div style={topBarStyle}>
        {!openPlanId && mediaPlansMode === 'list' && (
          <Button variant="primary" size="sm" onClick={() => navigateMediaPlans({ view: 'builder' })}>
            + New media plan
          </Button>
        )}
      </div>
    ),
    'rate-cards': (
      <div style={topBarStyle}>
        {canManagePlans && (
          <Button variant="primary" size="sm" onClick={() => setRateCardsAddSignal((n) => n + 1)}>
            + New rate card
          </Button>
        )}
      </div>
    ),
  };

  return (
    <DashboardFrame
      eyebrow="Sales · Planning & Pipeline"
      title="Sales"
      tabs={<Tabs value={tab} onChange={changeTab} options={tabOptions} variant="underlined" ariaLabel="Sales sections" />}
      topBarRight={topBarByTab[tab]}
    >
      <div style={{ padding: '20px 0' }}>
        {tab === 'pipeline' && (
          <OverviewTab
            overview={overview}
            overviewError={overviewError}
            overviewLoading={overviewLoading}
            ioDelivery={ioDelivery}
            ioError={ioError}
            ioLoading={ioLoading}
          />
        )}

        {mediaPlansVisited && (
          <div ref={mediaPlansScrollRef} style={tab === 'media-plans' ? undefined : { display: 'none' }} aria-hidden={tab !== 'media-plans'}>
            <MediaPlansTab
              active={tab === 'media-plans'}
              planId={openPlanId}
              mode={mediaPlansMode}
              chatDraftId={chatDraftId}
              registry={mediaPlanRegistry}
              onNavigate={navigateMediaPlans}
              canManagePlans={canManagePlans}
              notify={notify}
            />
          </div>
        )}

        {rateCardsVisited && (
          <div style={tab === 'rate-cards' ? undefined : { display: 'none' }} aria-hidden={tab !== 'rate-cards'}>
            <RateCardsTab registry={mediaPlanRegistry} canManage={canManagePlans} addSignal={rateCardsAddSignal} notify={notify} />
          </div>
        )}

        {tab === 'calendar' && (
          <CalendarTab
            tentpoles={tentpoles}
            entries={entries}
            loading={calendarLoading}
            loadError={calendarLoadError}
            onRetry={() => refreshCalendar({ initial: true })}
            hasMore={!!calendarNextCursor}
            filters={calFilters}
            onFiltersChange={setCalFilters}
            pendingCount={pendingCount}
            reviewEntries={reviewEntries}
            reviewLoading={reviewLoading}
            reviewHasMore={!!reviewNextCursor}
            reviewLoadingMore={reviewLoadingMore}
            onLoadMoreReview={loadMoreReviewEntries}
            onLoadMoreEntries={loadMoreEntries}
            loadingMoreEntries={loadingMoreEntries}
            canManage={canManage}
            onOpenEntry={openEntry}
            onEditTentpole={(t) => { setEditingTentpole(t); setShowTentpoleEditor(true); }}
            onAddEntryFor={openAddEntry}
            onAddTentpole={() => { setEditingTentpole(null); setShowTentpoleEditor(true); }}
          />
        )}
      </div>

      {openEntryId && (
        <EntryDetail
          entryId={openEntryId}
          canManage={canManage}
          summaryEntry={openEntrySummary}
          summaryTentpole={openEntryTentpole}
          onClose={closeEntry}
          onUpdated={onEntryUpdated}
          onDeleted={onEntryDeleted}
          onReviewChanged={() => refreshCalendar({ notifyOnError: false })}
          onEdit={(entry) => {
            closeEntry();
            setEditingEntry(entry);
            setPresetTentpoleSlug(null);
            setShowEntryEditor(true);
          }}
          onPromote={(entry) => {
            closeEntry();
            setPromoteSourceEntry(entry);
            setEditingTentpole(null);
            setShowTentpoleEditor(true);
          }}
          notify={notify}
        />
      )}

      {(showTentpoleEditor || editingTentpole) && (
        <TentpoleEditor
          tentpole={editingTentpole}
          preset={
            promoteSourceEntry
              ? {
                  name: promoteSourceEntry.customLabel ?? '',
                  category: 'culture',
                  startDate: promoteSourceEntry.plannedDate,
                  // The plan's whole span: a month plan its month(s), a week plan its week.
                  endDate: calendarEntryEffectiveEndYmd(promoteSourceEntry),
                }
              : undefined
          }
          onClose={() => {
            setShowTentpoleEditor(false);
            setEditingTentpole(null);
            setPromoteSourceEntry(null); // cancel abandons the promote
          }}
          onSaved={async (saved) => {
            // Promote: reassign the source entry onto the new tentpole (the
            // server clears customLabel). Only the source entry moves — other
            // entries sharing the label stay under ✦ Custom moments.
            if (saved && promoteSourceEntry) {
              try {
                await updateCalendarEntry(promoteSourceEntry.id, { tentpoleSlug: saved.slug });
                notify(
                  'success',
                  'Promoted to tentpole',
                  `“${promoteSourceEntry.customLabel}” is now ${saved.name}, and the plan moved to it.`,
                );
              } catch (err: any) {
                notify(
                  'error',
                  'Tentpole created, but moving the plan to it failed',
                  `Open the plan and set its moment to ${saved.name} by hand. ${err?.message ?? ''}`,
                );
              }
              setPromoteSourceEntry(null);
            }
            refreshCalendar({ notifyOnError: true });
          }}
          notify={notify}
        />
      )}

      {(showEntryEditor || editingEntry) && (
        <EntryEditor
          entry={editingEntry}
          tentpoles={tentpoles}
          presetTentpoleSlug={presetTentpoleSlug}
          onClose={() => { setShowEntryEditor(false); setEditingEntry(null); setPresetTentpoleSlug(null); }}
          onSaved={() => refreshCalendar({ notifyOnError: true })}
          onStale={() => refreshCalendar({ notifyOnError: false })}
          notify={notify}
        />
      )}

      {/* Fixed above the modal layer: most toasts here come from inside an
          open editor or detail modal. */}
      {toast && (
        <ToastLayer>
          <Toast kind={toast.kind} title={toast.title} message={toast.message} onClose={() => setToast(null)} />
        </ToastLayer>
      )}
    </DashboardFrame>
  );
};

const refreshBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 600,
  background: 'transparent',
  border: '1px solid var(--border-1)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  color: 'var(--fg-2)',
  fontFamily: 'var(--font-sans)',
};

const topBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const timestampStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-4)',
  fontFamily: 'var(--font-sans)',
  fontVariantNumeric: 'tabular-nums',
};

const staleTimestampStyle: React.CSSProperties = {
  color: 'var(--warning)',
  fontWeight: 600,
};

export default SalesDashboard;
